import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Users } from 'lucide-react';
import { DeductionsInstallateurs } from '@/components/installateurs/DeductionsInstallateurs';
import { JoursAdmissibles } from '@/components/installateurs/JoursAdmissibles';
import { ParametresInstallateurs } from '@/components/installateurs/ParametresInstallateurs';
import { TableauPerformance } from '@/components/installateurs/TableauPerformance';
import { FacturesInstallateurs } from '@/components/installateurs/FacturesInstallateurs';

const API = process.env.REACT_APP_BACKEND_URL;

// 🧾 Installateurs — Phase 1 (F3) : page squelette « Gestion des installateurs » (onglets remplis en Phases 2-3).
export default function GestionInstallateurs() {
  const [installateurs, setInstallateurs] = useState([]);
  const [erreur, setErreur] = useState('');
  const [versionParams, setVersionParams] = useState(0);  // 🧾 Phase 4 : un paramètre change → tableau Performance rechargé

  useEffect(() => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    axios.get(`${API}/api/installateurs`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setInstallateurs(r.data.installateurs || []))
      .catch(() => setErreur('Impossible de charger la liste des installateurs.'));
  }, []);

  return (
    <div className="p-6 space-y-6" data-testid="gestion-installateurs-page">
      <div className="flex items-center gap-3">
        <Users className="h-6 w-6 text-amber-600" />
        <h1 className="text-2xl font-bold" data-testid="gestion-installateurs-titre">Gestion des installateurs</h1>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Installateurs actifs ({installateurs.length})</CardTitle></CardHeader>
        <CardContent>
          {erreur && <p className="text-sm text-red-600" data-testid="gestion-installateurs-erreur">{erreur}</p>}
          <ul className="divide-y" data-testid="gestion-installateurs-liste">
            {installateurs.map((i) => (
              <li key={i.id} className="py-2 flex items-center justify-between gap-3" data-testid={`installateur-${i.id}`}>
                <div>
                  <div className="font-medium">{i.nom}</div>
                  <div className="text-xs text-slate-500">{i.nom_legal || 'Nom légal non renseigné'}{i.neq ? ` · NEQ ${i.neq}` : ''}</div>
                </div>
                <div className="flex items-center gap-2">
                  {i.avertissements?.length > 0 && (
                    <Badge variant="outline" className="text-amber-700 border-amber-300" title={i.avertissements.join(' · ')}>
                      <AlertTriangle className="h-3 w-3 mr-1" />Format à vérifier
                    </Badge>
                  )}
                  <Badge variant={i.statut === 'actif' ? 'default' : 'secondary'}>{i.statut}</Badge>
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Tabs defaultValue="deductions">
        <TabsList>
          <TabsTrigger value="deductions" data-testid="onglet-deductions">Déductions</TabsTrigger>
          <TabsTrigger value="jours" data-testid="onglet-jours-admissibles">Jours admissibles</TabsTrigger>
          <TabsTrigger value="performance" data-testid="onglet-performance-gestion">Performance</TabsTrigger>
          <TabsTrigger value="factures" data-testid="onglet-factures-gestion">Factures</TabsTrigger>
          <TabsTrigger value="parametres" data-testid="onglet-parametres">Paramètres</TabsTrigger>
        </TabsList>
        <TabsContent value="performance">
          <TableauPerformance rafraichir={versionParams} />
        </TabsContent>
        <TabsContent value="parametres">
          <ParametresInstallateurs onChange={() => setVersionParams((v) => v + 1)} />
        </TabsContent>
        <TabsContent value="factures">
          <FacturesInstallateurs />
        </TabsContent>
        <TabsContent value="deductions">
          <DeductionsInstallateurs installateurs={installateurs} />
        </TabsContent>
        <TabsContent value="jours">
          <JoursAdmissibles installateurs={installateurs} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
